import { useState, useEffect, useRef } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Html5Qrcode } from 'html5-qrcode'
import './QRScan.css'
import RequestFab from '../components/RequestFab'

const QRScanner = () => {
  const navigate = useNavigate()
  const [scanning, setScanning] = useState(false)
  const [error, setError] = useState('')
  const [requestMenuOpen, setRequestMenuOpen] = useState(false)
  const html5QrCodeRef = useRef(null)

  useEffect(() => {
    return () => {
      if (html5QrCodeRef.current) {
        const instance = html5QrCodeRef.current
        html5QrCodeRef.current = null
        void (async () => {
          try {
            await instance.stop()
          } catch {
            /* already stopped */
          }
          try {
            await instance.clear()
          } catch {
            /* ignore */
          }
        })()
      }
    }
  }, [])

  const extractUUIDFromURL = (url) => {
    try {
      const urlObj = new URL(url, window.location.origin)
      const pathParts = urlObj.pathname.split('/')
      const qrIndex = pathParts.indexOf('qr')

      if (qrIndex !== -1 && pathParts[qrIndex + 1]) {
        return pathParts[qrIndex + 1]
      }

      const qrMatch = url.match(/\/qr\/([a-f0-9-]{36})/i)
      if (qrMatch) {
        return qrMatch[1]
      }

      const uuidMatch = url.match(/^([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})$/i)
      if (uuidMatch) {
        return uuidMatch[1]
      }

      return null
    } catch (e) {
      const qrMatch = url.match(/\/qr\/([a-f0-9-]{36})/i)
      if (qrMatch) {
        return qrMatch[1]
      }

      const uuidMatch = url.match(/^([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})$/i)
      if (uuidMatch) {
        return uuidMatch[1]
      }

      return null
    }
  }

  const handleScanSuccess = (decodedText) => {
    const uuid = extractUUIDFromURL(decodedText)

    if (uuid) {
      stopScanning()
      setTimeout(() => {
        navigate(`/qr/${uuid}`)
      }, 300)
    } else {
      setError('Not a NAH44 QR code. Try another.')
      setTimeout(() => setError(''), 4000)
    }
  }

  /**
   * #qr-reader must stay empty — only html5-qrcode injects nodes there (React would wipe them).
   */
  const startScanning = async () => {
    if (!window.isSecureContext) {
      setError('Use HTTPS or localhost so the browser can open the camera.')
      return
    }

    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop()
        await html5QrCodeRef.current.clear()
      } catch (e) {
        /* ignore */
      }
      html5QrCodeRef.current = null
    }

    setError('')

    const element = document.getElementById('qr-reader')
    if (!element) {
      setError('Scanner not ready. Refresh the page.')
      return
    }

    const html5QrCode = new Html5Qrcode('qr-reader')
    html5QrCodeRef.current = html5QrCode

    const config = {
      fps: 10,
      qrbox: { width: 250, height: 250 },
      aspectRatio: 1.0
    }

    const onSuccess = (decodedText) => {
      handleScanSuccess(decodedText)
    }
    const onError = (errorMessage) => {
      if (!errorMessage.includes('NotFoundException') && !errorMessage.includes('No MultiFormat Readers')) {
        console.debug('Scan error (ignored):', errorMessage)
      }
    }

    const tryStart = async (cameraIdOrConfig) => {
      await html5QrCode.start(cameraIdOrConfig, config, onSuccess, onError)
    }

    try {
      try {
        await tryStart({ facingMode: 'environment' })
      } catch (firstErr) {
        await html5QrCode.clear().catch(() => {})
        const retriable =
          firstErr?.name === 'NotFoundError' ||
          firstErr?.name === 'DevicesNotFoundError' ||
          firstErr?.name === 'OverconstrainedError' ||
          (firstErr?.message && /not found|constraint|device/i.test(firstErr.message))
        if (!retriable) throw firstErr
        try {
          await tryStart({ facingMode: 'user' })
        } catch (secondErr) {
          await html5QrCode.clear().catch(() => {})
          const cameras = await Html5Qrcode.getCameras()
          if (!cameras?.length) throw secondErr
          await tryStart(cameras[0].id)
        }
      }

      setScanning(true)
    } catch (err) {
      console.error('Error starting scanner:', err)
      setScanning(false)

      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setError('Camera access was blocked. Tap Scan again and choose Allow, or enable camera for this site in browser settings.')
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setError('No camera found on this device.')
      } else {
        setError(err.message || 'Could not start the camera. Try again.')
      }

      if (html5QrCodeRef.current) {
        try {
          await html5QrCodeRef.current.stop().catch(() => {})
          await html5QrCodeRef.current.clear().catch(() => {})
        } catch (e) {
          /* ignore */
        }
        html5QrCodeRef.current = null
      }
    }
  }

  const stopScanning = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop()
        await html5QrCodeRef.current.clear()
      } catch (err) {
        console.error('Error stopping scanner:', err)
      } finally {
        html5QrCodeRef.current = null
        setScanning(false)
      }
    } else {
      setScanning(false)
    }
  }

  const handleScanTap = () => {
    if (scanning) {
      stopScanning()
    } else {
      startScanning()
    }
  }

  const secure = typeof window !== 'undefined' && window.isSecureContext

  return (
    <div className="qr-page">
      <div className="qr-container">
        <div className="qr-card">
          <div className="qr-header">
            <h2 className="qr-title">Scan QR</h2>
            <p style={{ color: 'var(--text-secondary)', marginTop: '0.5rem', fontSize: '0.95rem' }}>
              Tap the button below. If your browser asks, allow the camera — then point at a NAH44 code.
            </p>
          </div>

          {error && (
            <div className="status-card status-error" style={{ marginBottom: '1rem' }}>
              <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '0.9rem' }}>{error}</p>
            </div>
          )}

          {!secure && (
            <div className="status-card status-warning" style={{ marginBottom: '1rem' }}>
              <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '0.9rem' }}>
                Open this app on <strong>https://</strong>, <strong>localhost</strong>, or <strong>127.0.0.1</strong> — not a LAN IP over http — or the camera cannot start.
              </p>
            </div>
          )}

          <div className="action-section" style={{ marginBottom: '1.25rem' }}>
            <div
              style={{
                position: 'relative',
                width: '100%',
                minHeight: '280px',
                border: scanning ? '2px solid var(--primary-color)' : '2px dashed rgba(255, 255, 255, 0.2)',
                borderRadius: '1rem',
                overflow: 'hidden',
                backgroundColor: scanning ? 'transparent' : 'rgba(0, 0, 0, 0.2)'
              }}
            >
              <div id="qr-reader" style={{ width: '100%', minHeight: '280px' }} />
              {!scanning && (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    padding: '24px',
                    color: 'var(--text-secondary)',
                    pointerEvents: 'none',
                    zIndex: 1
                  }}
                >
                  <div>
                    <div style={{ fontSize: '56px', marginBottom: '0.75rem', opacity: 0.85 }}>📷</div>
                    <p style={{ margin: 0, fontSize: '0.9rem' }}>Tap &quot;Scan now&quot; to open the camera</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="action-section">
            <button
              type="button"
              className={scanning ? 'btn btn-secondary' : 'btn btn-primary'}
              onClick={handleScanTap}
              disabled={!secure}
              style={{
                width: '100%',
                padding: '0.9rem 1.25rem',
                fontSize: '1.05rem',
                fontWeight: '600'
              }}
            >
              {scanning ? 'Stop' : 'Scan now'}
            </button>

            {!scanning && (
              <Link
                to="/"
                className="btn btn-text"
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'center',
                  marginTop: '0.75rem',
                  padding: '0.5rem',
                  color: 'var(--text-secondary)',
                  textDecoration: 'none',
                  fontSize: '0.9rem'
                }}
              >
                ← Home
              </Link>
            )}
          </div>
        </div>
      </div>
      <RequestFab
        isOpen={requestMenuOpen}
        onClick={() => setRequestMenuOpen(!requestMenuOpen)}
      />
    </div>
  )
}

export default QRScanner
